<template>
  <Drawer
    :visible="menu.isOpen"
    position="right"
    class="app-menu"
    :aria-label="word('shell.menu.label')"
    data-app-menu
    @update:visible="menu.close"
  >
    <!-- The whole panel is drawn here, in Drawer's `container` slot, so there
         is no header and no close button. A press outside the panel and the
         Escape key are how it closes. -->
    <template #container>
      <div class="app-menu__panel">
        <!-- A phone app that opens the panel at full width has no strip of page
             left to press and no Escape key, so it asks for this close control.
             Off by default: the desktop panel leaves the page to press. -->
        <div v-if="closable" class="app-menu__top">
          <button
            type="button"
            class="app-menu__close"
            :aria-label="word('shell.menu.close')"
            data-app-menu-close
            @click="menu.close()"
          >
            <i class="pi pi-times" aria-hidden="true" />
          </button>
        </div>
        <!-- Who is signed in. Standard, so every app shows it the same way;
             an app that has nobody to show turns it off. When a `profile` entry
             is registered the bar is that entry's door, so it is a real button
             and reachable by keyboard and screen reader; otherwise it is a
             label. -->
        <button
          v-if="profileEntry"
          type="button"
          class="app-menu__account app-menu__account--door"
          :aria-label="`${profileEntry.label}, ${name}`"
          :data-menu-entry="profileEntry.id"
          data-app-menu-account
          @click="choose(profileEntry)"
        >
          <AvatarChip :initial="initial" :seed="seed" :size="46" />
          <span class="app-menu__name">{{ name }}</span>
          <span v-if="badge" class="app-menu__badge">{{ badge }}</span>
        </button>
        <div v-else-if="showsAccount" class="app-menu__account" data-app-menu-account>
          <AvatarChip :initial="initial" :seed="seed" :size="46" />
          <span class="app-menu__name">{{ name }}</span>
          <span v-if="badge" class="app-menu__badge">{{ badge }}</span>
        </div>

        <section
          v-for="section in listSections"
          :key="section.group"
          class="app-menu__group"
          :class="{ 'app-menu__group--starts-bottom': section.group === bottomStartGroup }"
        >
          <ul class="app-menu__list">
            <li v-for="entry in section.entries" :key="entry.id">
              <button
                type="button"
                class="app-menu__row"
                :aria-label="nameOf(entry)"
                :data-menu-entry="entry.id"
                @click="choose(entry)"
              >
                <span
                  v-if="entry.icon"
                  class="app-menu__icon"
                  :class="{ 'app-menu__icon--accent': entry.tone === 'accent' }"
                >
                  <i :class="`pi pi-${entry.icon}`" aria-hidden="true" />
                  <span v-if="entry.attention" class="app-menu__attention" aria-hidden="true" />
                </span>
                <span class="app-menu__body">
                  <span class="app-menu__label">{{ entry.label }}</span>
                  <span v-if="entry.sub" class="app-menu__subtitle">{{ entry.sub }}</span>
                </span>
                <span v-if="countOf(entry)" class="app-menu__count" aria-hidden="true">{{ countOf(entry) }}</span>
                <i class="pi pi-chevron-right app-menu__chevron" aria-hidden="true" />
              </button>
            </li>
          </ul>
        </section>

        <!-- Danger entries leave the list and become full-width buttons. Log
             Out is the only one today, and it reads as an action rather than a
             destination — so the shell makes that a rule of `tone` instead of
             each app styling its own footer. -->
        <div
          v-if="dangerEntries.length"
          class="app-menu__footer"
          :class="{ 'app-menu__footer--starts-bottom': bottomStartGroup === null }"
        >
          <Button
            v-for="entry in dangerEntries"
            :key="entry.id"
            class="app-menu__danger"
            :icon="entry.icon ? `pi pi-${entry.icon}` : undefined"
            :label="entry.label"
            :data-menu-entry="entry.id"
            @click="choose(entry)"
          />
        </div>
      </div>
    </template>
  </Drawer>
</template>

<script setup>
import Button from 'primevue/button';
import Drawer from 'primevue/drawer';
import { computed, inject } from 'vue';
import { routerKey } from 'vue-router';
import { AvatarChip } from '@hds/auto-auction-com-components-shared';
import { sessionStore } from '@hds/identity-client-sdk';

import { useAppMenuStore } from './appMenuStore.js';
import { useLanguageStore } from './languageStore.js';
import { useShellWords } from './shellWords.js';
import { useThemeStore } from './themeStore.js';

// The App Menu: the drawer that slides in from the right edge of the viewport,
// on a phone and on a desktop alike. It draws what the App Menu store holds, so
// the same shell serves an app with four rows and an app with fourteen.
//
// It is baked in. An app places it and passes nothing: who is signed in comes
// from the session, the words come from the package, and a row that names a
// route is opened through the app's own router.
//
// It has no `open` prop and exposes no method. Whether it is open lives in the
// store, so anything in the app opens or closes it with `useAppMenuStore()`.

const props = defineProps({
    // The bar at the top that says who is signed in. On unless an app turns it
    // off; with nobody signed in there is nobody to show, and it is not drawn.
    accountBar: { type: Boolean, default: true },
    // A close control at the top of the panel. Off unless an app opens the
    // panel at full width (the phone portal does), where nothing else closes it.
    closable: { type: Boolean, default: false },
    // A route row leaves the menu open. Off unless an app opens a row's page over
    // the menu and closes the menu itself once that page covers it (the phone
    // portal does), so Back can land on the menu where the person left it.
    leaveOpenOnRoute: { type: Boolean, default: false }
});

const emit = defineEmits(['select']);

// The group the account doors register in. An app's own rows come before it.
const ACCOUNT_GROUP = 'account';

// The entry the account bar stands in for. The bar already says who is signed
// in, so it is the door to that person's profile rather than a label above a
// row that says the same thing.
const PROFILE_ENTRY = 'profile';

// The most a count pill says. Past this the exact number stops being the point,
// and a four-digit pill would push the row's label out of the row.
const COUNT_CAP = 99;

const menu = useAppMenuStore();
const session = sessionStore();
const word = useShellWords();

// The drawer is the one piece of the shell an app always mounts, so it is what
// wakes the person's saved theme and language. Each store applies what was
// saved when it is first used; without this, a preference would not show until
// somebody opened Settings.
useThemeStore();
useLanguageStore();

// The app's router, when it has one. Read with `inject` rather than
// `useRouter()`, which warns when there is none — and a Storybook has none.
const router = inject(routerKey, null);

// Who is signed in, as the session knows them.
const name = computed(() => session.user?.displayName ?? session.user?.name ?? '');
const seed = computed(() => String(session.user?._id ?? session.user?.id ?? ''));
const badge = computed(() => session.user?.badge ?? '');

const showsAccount = computed(() => props.accountBar && name.value.length > 0);

const initial = computed(() => name.value.trim().charAt(0).toUpperCase());

// The profile entry, when the bar is drawn in its place. With the bar turned
// off — or nobody to show in it — this is null and the entry is an ordinary row,
// so the door to the profile never disappears with the bar.
const profileEntry = computed(function () {
    if (!showsAccount.value) {
        return null;
    }
    return menu.entries.find((entry) => entry.id === PROFILE_ENTRY) ?? null;
});

const dangerEntries = computed(
    () => menu.entries.filter((entry) => entry.tone === 'danger')
);

// What a row's count pill reads, or null for no pill at all. Zero draws
// nothing — a row with nothing behind it is quieter without a badge than with a
// `0` on it — and a source that has not answered is null for the same reason,
// so neither ever claims a number the store cannot stand behind.
function countOf(entry) {
    if (!entry.count) {
        return null;
    }
    if (entry.count > COUNT_CAP) {
        return `${COUNT_CAP}+`;
    }
    return String(entry.count);
}

// The row's accessible name, when it carries a pill. The pill itself is
// `aria-hidden`, so the number reaches a screen reader through here instead: a
// row that reads "Messages" while it shows a 4 keeps the count from the one
// person who cannot see it. Undefined for a row with no pill, which leaves the
// attribute off and lets the button be named by its own text.
function nameOf(entry) {
    const count = countOf(entry);
    if (!count) {
        return undefined;
    }
    return word('shell.menu.rowCount').replace('{label}', entry.label).replace('{count}', count);
}

// A danger entry belongs to the footer and the profile entry to the account
// bar, so both leave the lists — otherwise each renders twice. A group that
// held nothing else disappears with it rather than leaving an empty card.
const listSections = computed(() => menu.sections
    .map((section) => ({
        group: section.group,
        entries: section.entries.filter((entry) => entry.tone !== 'danger' && entry !== profileEntry.value)
    }))
    .filter((section) => section.entries.length > 0));

// An app's own rows sit at the top of the drawer. The account doors, anything
// after them, and the way out sit at the bottom, so the same doors are in the
// same place whether an app adds no rows or ten. This names the first group of
// that bottom block; when there is none, the footer is what goes to the bottom.
const bottomStartGroup = computed(function () {
    if (listSections.value.some((section) => section.group === ACCOUNT_GROUP)) {
        return ACCOUNT_GROUP;
    }
    return null;
});

// The drawer reports its own dismissals — a press outside the panel, Escape —
// as `update:visible`, and only ever with `false`: it has no way to open
// itself. So that report is the store's `close`, and the two never disagree
// about whether the menu is open.

// Close first, then act. A drawer left open over the page the person just
// asked for is the failure this order prevents. An action entry runs its own
// action; a route entry is opened through the app's router. `select` reports
// the choice either way, for a host that wants to observe it. A host that set
// `leaveOpenOnRoute` takes over closing for a route entry — except a row for the
// page already showing, which only closes the menu: the router skips a move to
// where it already is, so the host would never hear of it.
function choose(entry) {
    if (entry.onSelect || !props.leaveOpenOnRoute || isCurrentRoute(entry.to)) {
        menu.close();
    }
    emit('select', entry);
    if (entry.onSelect) {
        entry.onSelect();
        return;
    }
    if (router && !isCurrentRoute(entry.to)) {
        router.push(entry.to);
    }
}

// Whether a row's route is the page already showing. Compared by path, so a row
// for the Dashboard counts as current on the Dashboard whatever its query says.
function isCurrentRoute(to) {
    if (!router || to === undefined) {
        return false;
    }
    return router.resolve(to).path === router.currentRoute.value.path;
}
</script>

<style scoped>
/* The drawer is teleported to <body>, so this component's scope attribute never
   reaches its root and a scoped selector cannot size it. It is a fixed column
   on the right; on a phone it is three quarters of the width, which leaves a
   strip of the page to press — the only way out besides Escape.

   `.p-drawer-right` is in the selector to outrank PrimeVue's own width rule,
   which has the same weight as `.app-menu.p-drawer` and is injected later. */
:global(.p-drawer-right .app-menu.p-drawer) {
  width: 22.5rem;
}

@media (max-width: 480px) {
  :global(.p-drawer-right .app-menu.p-drawer) {
    width: 75%;
  }
}

/* Frost the page behind the panel, as the apps' sheets do. The theme's mask is
   a flat dim; the blur is what makes the drawer read as glass over the page. */
:global(.p-drawer-mask:has(.app-menu)) {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* The theme gives a Drawer no padding at all: the host draws its own inset.
   This is that inset, and on a phone it clears the notch and the home
   indicator. */
.app-menu__panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  padding: max(20px, env(safe-area-inset-top)) 20px max(20px, env(safe-area-inset-bottom));
}

.app-menu__top {
  display: flex;
  justify-content: flex-end;
  margin: -8px -8px 8px;
}

.app-menu__close {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: none;
  color: var(--p-text-color);
  font-size: 18px;
  cursor: pointer;
}

.app-menu__close:hover {
  background: var(--p-content-hover-background);
}

.app-menu__close:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 2px;
}

.app-menu__account {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  padding: 16px;
  background: var(--p-content-background);
  border: 1px solid var(--p-content-border-color);
  border-radius: 16px;
}

/* The bar as a button: the same card, with a button's defaults taken off. */
.app-menu__account--door {
  width: 100%;
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.app-menu__account--door:hover {
  background: var(--p-content-hover-background);
}

.app-menu__name {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 700;
}

.app-menu__badge {
  padding: 4px 9px;
  border-radius: 999px;
  background: var(--p-highlight-background);
  color: var(--p-primary-color);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.app-menu__group {
  flex-shrink: 0;
  margin-bottom: 14px;
  background: var(--p-content-background);
  border: 1px solid var(--p-content-border-color);
  border-radius: 16px;
  overflow: hidden;
}

/* Exactly one element takes the free space above it, which is what puts the
   account doors and the way out at the bottom. Two would split it between
   them. When the rows outgrow the drawer there is no free space, and the panel
   simply scrolls. */
.app-menu__group--starts-bottom,
.app-menu__footer--starts-bottom {
  margin-top: auto;
}

.app-menu__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.app-menu__list li + li {
  border-top: 1px solid var(--p-content-border-color);
}

.app-menu__row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  border: 0;
  background: transparent;
  color: var(--p-text-color);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.app-menu__row:hover {
  background: var(--p-content-hover-background);
}

.app-menu__icon {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: var(--p-content-hover-background);
  color: var(--p-text-hover-muted-color);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.app-menu__icon--accent {
  background: var(--p-highlight-background);
  color: var(--p-primary-color);
}

.app-menu__icon i {
  font-size: 17px;
}

/* The attention dot: something on that surface is waiting. */
.app-menu__attention {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--p-primary-color);
  border: 2px solid var(--p-content-background);
}

.app-menu__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.app-menu__label {
  font-size: 14px;
  font-weight: 600;
}

.app-menu__subtitle {
  margin-top: 2px;
  font-size: 12px;
  color: var(--p-text-muted-color);
}

/* The count pill: how many things are behind that row. The attention dot says
   there is something there; this says how much. It sits between the label and
   the chevron, which the body's `flex: 1` pushes it against. */
.app-menu__count {
  flex-shrink: 0;
  min-width: 22px;
  padding: 2px 7px;
  border-radius: 999px;
  background: var(--p-highlight-background);
  color: var(--p-primary-color);
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.app-menu__chevron {
  font-size: 15px;
  color: var(--p-text-muted-color);
}

.app-menu__footer {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* A PrimeVue Button, the same component path as every other call to action in
   these apps. `.p-button` lifts this paint over PrimeVue's own rules; the menu
   draws no hover or press state of its own, so both restate the resting paint
   with the border SHORTHAND in it, which PrimeVue's own rules set. */
.p-button.app-menu__danger,
.p-button.app-menu__danger:not(:disabled):hover,
.p-button.app-menu__danger:not(:disabled):active {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 52px;
  border: 1px solid var(--p-content-border-color);
  border-radius: 14px;
  background: var(--p-content-background);
  color: var(--p-red-400);
  font-family: inherit;
  font-size: 15px;
  cursor: pointer;
}

.app-menu__danger :deep(.p-button-label) {
  font-weight: 600;
}

.app-menu__danger :deep(.p-button-icon) {
  font-size: 16px;
}

/* A phone: the same drawer, sized for a finger. */
@media (max-width: 480px) {
  .app-menu__row {
    padding: 14px 16px;
  }

  .app-menu__label {
    font-size: 15px;
  }

  .app-menu__chevron {
    font-size: 17px;
  }
}
</style>
